// src/services/serverService.js

import axios from 'axios';
import { API_BASE_URL } from '../config';
import { attachAuthInterceptors } from './attachAuthInterceptors';
import { refreshSession, reauthenticateTelegram } from './authService';

console.log('🌐 ServerService baseURL:', API_BASE_URL);

// === Инициализация axios ===
const api = axios.create({ baseURL: API_BASE_URL });
attachAuthInterceptors(api, { refreshSession, reauthenticateTelegram });

// === Универсальная обработка ответов ===
const handleResponse = (response, key) => {
  if (response.status === 200 && response.data[key]) {
    return typeof response.data[key] === 'string'
      ? JSON.parse(response.data[key])
      : response.data[key];
  }
  throw new Error(response.data.message || `Ошибка при обработке ответа на ${key}`);
};

// === Универсальная обработка ошибок ===
const handleError = (error, actionDescription) => {
  console.error(`❌ Ошибка при выполнении запроса (${actionDescription}):`, error.message);
  throw error;
};

// === Получение списка серверов ===
export const fetchServers = async (token) => {
  try {
    const response = await api.get('/servers/list', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return handleResponse(response, 'servers');
  } catch (error) {
    handleError(error, 'получение списка серверов');
  }
};

// === Получение активного сервера ===
export const fetchActiveServer = async (token) => {
  try {
    const response = await api.get('/servers/active', {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status < 500, // не выбрасывать при 4xx
    });

    // Если сервер не найден или пуст
    if (response.status === 404 || !response.data?.active_server) {
      console.log('ℹ️ Активный сервер отсутствует (новый пользователь)');
      return null;
    }

    // Если всё корректно
    if (response.status === 200 && response.data.active_server) {
      const activeServer =
        typeof response.data.active_server === 'string'
          ? JSON.parse(response.data.active_server)
          : response.data.active_server;

      // ✅ Сохраняем активный сервер в localStorage для useMetricsData
      localStorage.setItem("activeServer", JSON.stringify(activeServer));

      // ✅ Триггерим кастомное событие для уведомления компонентов об изменении активного сервера
      window.dispatchEvent(new CustomEvent("activeServerChanged"));

      return activeServer;
    }

    console.warn('⚠️ Неожиданный статус при получении активного сервера:', response.status);
    return null;
  } catch (error) {
    console.warn('⚠️ Ошибка при запросе активного сервера (мягкая):', error?.message || error);
    return null;
  }
};

// === Установка активного сервера ===
export const setActiveServer = async (serverId, token) => {
  try {
    const response = await api.post(`/servers/set_active/${serverId}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`Ошибка при установке активного сервера (ID: ${serverId}): ${response.data}`);
    }
  } catch (error) {
    handleError(error, `установка активного сервера (ID: ${serverId})`);
  }
};

// === Создание сервера ===
export const createServer = async (serverData, token) => {
  try {
    const response = await api.post('/servers/create', serverData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`Ошибка при добавлении сервера: ${response.data}`);
    }
  } catch (error) {
    handleError(error, 'создание сервера');
  }
};

// === Обновление сервера ===
export const updateServer = async (serverId, serverData, token) => {
  try {
    const response = await api.put(`/servers/update/${serverId}`, serverData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`Ошибка при редактировании сервера (ID: ${serverId}): ${response.data}`);
    }
  } catch (error) {
    handleError(error, `редактирование сервера (ID: ${serverId})`);
  }
};

// === Удаление сервера ===
export const deleteServer = async (serverId, token) => {
  try {
    const response = await api.delete(`/servers/delete/${serverId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`Ошибка при удалении сервера (ID: ${serverId}): ${response.data}`);
    }
  } catch (error) {
    handleError(error, `удаление сервера (ID: ${serverId})`);
  }
};

export default api;
